import { Schema } from "@effect/schema";
import { Address, BaseError, ChainKey, TokenUid, Usd } from "@silo/types";
import { UsdFromDecimal } from "@silo/utils";

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// errors
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export class CmcClientError extends BaseError {
  public readonly _tag = "CmcClientError";
}

export class InvalidSymbolError extends BaseError {
  public readonly _tag = "InvalidSymbolError";
  public readonly invalidSymbols: string[];

  constructor({ invalidSymbols }: { invalidSymbols: string[] }) {
    super();
    this.invalidSymbols = invalidSymbols;
  }
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// CMC API types
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export type CmcStatus = typeof CmcStatus.Type;
export type CmcStatusEncoded = typeof CmcStatus.Encoded;

export const CmcStatus = Schema.Struct({
  status: Schema.Struct({
    error_code: Schema.Number,
    error_message: Schema.NullOr(Schema.String),
    credit_count: Schema.Number,
  }),
  data: Schema.optional(Schema.Unknown),
}).annotations({ identifier: "CmcStatus" });

export type Metadata = typeof Metadata.Type;
export type MetadataEncoded = typeof Metadata.Encoded;

export const Metadata = Schema.Struct({
  id: Schema.Number,
  platform: Schema.NullOr(
    Schema.Struct({
      id: Schema.NumberFromString,
      token_address: Schema.String,
    })
  ),
  logo: Schema.String,
}).annotations({ identifier: "Metadata" });

export type MetadataBySymbol = typeof MetadataBySymbol.Type;
export type MetadataBySymbolEncoded = typeof MetadataBySymbol.Encoded;

export const MetadataBySymbol = Schema.Record({
  key: Schema.String,
  value: Schema.UndefinedOr(
    Schema.Array(
      Schema.extend(
        Metadata,
        Schema.Struct({
          contract_address: Schema.Array(
            Schema.Struct({
              contract_address: Schema.String,
            })
          ),
        })
      )
    )
  ),
}).annotations({ identifier: "MetadataBySymbol" });

export type MetadataByAddress = typeof MetadataByAddress.Type;
export type MetadataByAddressEncoded = typeof MetadataByAddress.Encoded;

export const MetadataByAddress = Schema.Record({
  key: Schema.String,
  value: Metadata,
}).annotations({ identifier: "MetadataByAddress" });

export type Quotes = typeof Quotes.Type;
export type QuotesEncoded = typeof Quotes.Encoded;

export const Quotes = Schema.Record({
  key: Schema.String,
  value: Schema.UndefinedOr(
    Schema.Struct({
      quote: Schema.Struct({
        USD: Schema.Struct({
          price: UsdFromDecimal,
        }),
      }),
    })
  ),
}).annotations({ identifier: "Quotes" });

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// internal types
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export type CmcTokenMeta = typeof CmcTokenMeta.Type;
export type CmcTokenMetaEncoded = typeof CmcTokenMeta.Encoded;

export const CmcTokenMeta = Schema.Struct({
  id: Schema.Number,
  platform: Schema.NullOr(
    Schema.Struct({
      id: Schema.Number,
      tokenAddress: Address,
    })
  ),
  logo: Schema.String,
}).annotations({ identifier: "CmcTokenMeta" });

export type CmcToken = typeof CmcToken.Type;
export type CmcTokenEncoded = typeof CmcToken.Encoded;

export const CmcToken = Schema.extend(
  TokenUid,
  Schema.Struct({
    meta: CmcTokenMeta,
  })
).annotations({ identifier: "CmcToken" });

export type CmcTokenWithPrice = typeof CmcTokenWithPrice.Type;
export type CmcTokenWithPriceEncoded = typeof CmcTokenWithPrice.Encoded;

export const CmcTokenWithPrice = Schema.extend(
  CmcToken,
  Schema.Struct({
    priceUsd: Usd,
  })
).annotations({ identifier: "CmcTokenWithPrice" });
